import {
  Circle, Container, DataItem, ISpritePointerEvent, Label,
  Root,
} from '@amcharts/amcharts5';
import {
  ClusteredPointSeries, IMapPointSeriesDataItem, MapChart, MapPointSeries, MapPolygonSeries, ZoomControl,
} from '@amcharts/amcharts5/map';
import { HealthUnitsInfo } from '../contexts/HealthDataContext';
import am5geodata_brazilLow from '@amcharts/amcharts5-geodata/brazilLow';
import { bulletEmptyInfoPosition } from './constants';
import { MapUnitInfo } from './types';
import { ThermalComfortUnitsInfo } from '../contexts/ThermalComfortDataContext';
import { UnitMap } from './MapContent';
import { parseHealthColor } from '../helpers/health';
import { parseThermalComfortColor } from '../helpers/thermalComfort';

export function onPointerOverStateMarker(
  event: ISpritePointerEvent & {
            type: 'pointerover';
            target: Container;
        },
): void {
  applyClassStateMarker('disabledHover', event.target.parent, event.target.uid);
}

export function onPointerOutStateMarker(
  event: ISpritePointerEvent & {
            type: 'pointerout';
            target: Container;
        },
): void {
  applyClassStateMarker('default', event.target.parent, event.target.uid);
}

export function applyClassStateMarker(className: string, container: Container | undefined, containerId: number): void {
  if (container) {
    container.eachChildren((children) => {
      const containerChildren = children as Container;

      if (children.uid !== containerId) {
        const circle = containerChildren.children.values.find(
          (item) => item instanceof Circle,
        );
        const label = containerChildren.children.values.filter(
          (item) => item instanceof Label,
        );

        if (circle && label) {
          circle.states.applyAnimate(className);
          for (const item of label) {
            item.states.apply(className);
          }
        }
      }
    });
  }
}

export function addUnitPoint(
  pointSeries: ClusteredPointSeries,
  unit: {
    latitude: number,
    longitude: number,
    name: string,
    state: string,
    id: string,
    color: string,
    city: string,
  },
): void {
  pointSeries.data.push({
    geometry: {
      type: 'Point',
      coordinates: [unit.longitude, unit.latitude],
    },
    group: `group-${unit.state}`,
    title: unit.name,
    unitId: unit.id,
    color: unit.color,
    state: unit.state,
    city: unit.city,
  });
}

export function buildStateMapMarkers(
  clusteredSeries: ClusteredPointSeries,
  units: MapUnitInfo[],
): void {
  for (const unit of units) {
    if (!unit.latitude || !unit.longitude) continue;

    addUnitPoint(
      clusteredSeries,
      {
        color: unit.color,
        id: String(unit.id),
        latitude: unit.latitude,
        longitude: unit.longitude,
        name: unit.name,
        state: unit.state,
        city: unit.city,
      },
    );
  }
}

export function buildCountryMapMarkers(root: Root, pointSeries: MapPointSeries, map: Map<string, MapUnitInfo[]>, disabled: boolean): void {
  for (const feature of am5geodata_brazilLow.features) {
    const { id } = feature;
    const [, state] = (id as string).split('-');
    const value = map.get(state)?.length ?? 0;

    if (value === 0 && !disabled) {
      continue;
    }

    pointSeries.pushDataItem({
      polygonId: feature.id as string,
      value,
    });
  }

  const emptyUnits = map.get('empty')?.length ?? 0;

  if (emptyUnits === 0) {
    return;
  }

  const dataItemInfo = {
    ...bulletEmptyInfoPosition,
    value: emptyUnits,
  };

  pointSeries.pushDataItem(dataItemInfo, {
    empty: true,
  });
}

export async function buildStateMap(
  polygonId: string,
  chart: MapChart,
  statePolygonSeries: MapPolygonSeries,
  zoomControl: ZoomControl,
  backButton: Container,
): Promise<void> {
  const findFeature = am5geodata_brazilLow.features.find(
    (item) => item.id === polygonId,
  );

  if (findFeature) {
    statePolygonSeries.data.push(findFeature);
    const newDataItem = statePolygonSeries.getDataItemById(findFeature.id as string);

    if (newDataItem) {
      const showAnimation = statePolygonSeries.zoomToDataItem(newDataItem);
      await showAnimation?.waitForStop();

      chart.setAll({
        panX: 'rotateX',
        panY: 'rotateY',
        wheelable: true,
        pinchZoom: true,
        wheelY: 'zoom',
        maxPanOut: 0.1,
      });
      zoomControl.show(50);

      if (!backButton.isVisible()) {
        backButton.show(50);
      }
    }
  }
}

export function getStateNameByPolygon(event: ISpritePointerEvent & { target: Container }): string {
  const dataItem = event.target.dataItem as DataItem<IMapPointSeriesDataItem>;
  const polygonId = dataItem.get('polygonId') as string;
  const [, stateName] = polygonId.split('-');

  return stateName;
}

export function getUnitIdByMarker(event: ISpritePointerEvent & { target: Container }): string {
  const dataItem = event.target.dataItem as DataItem<IMapPointSeriesDataItem>;
  const unitId = dataItem.get('unitId' as unknown as keyof IMapPointSeriesDataItem) as string;

  return unitId;
}

export function onClickUnitMarker(
  event: ISpritePointerEvent & {
            type: 'click';
            target: Container;
        },
): void {
  const containerId = event.target.uid;
  const parent = event.target.parent;

  if (parent) {
    parent.eachChildren((children) => {
      const containerChildren = children as Container;
      for (const child of containerChildren.children.values) {
        const childContainer = child as Container;
        childContainer.states.apply(children.uid === containerId ? 'default' : 'disabled');
      }
    });
  }
}

export function applyClassOthersUnitMarker(className: string, parent: Container | undefined, containerId: number): void {
  if (parent) {
    parent.eachChildren((children) => {
      const containerChildren = children as Container;
      if (children.uid !== containerId) {
        for (const child of containerChildren.children.values) {
          const childContainer = child as Container;
          childContainer.states.apply(className);
        }
      }
    });
  }
}

export function sameStatesArray(previousStates: string[], currentStates: string[]): boolean {
  if (previousStates.length !== currentStates.length) return false;

  const setPrevious = new Set(previousStates);
  const setCurrent = new Set(currentStates);

  return [...setPrevious].every((item) => setCurrent.has(item));
}

export function buildUnitMap<TypeMap extends { state: string, lat?: number | null, lon?: number | null }[]>(
  units: TypeMap,
): Map<string, TypeMap> {
  const groupMap = new Map<string, typeof units>();

  for (const unit of units) {
    if (!unit.lat || !unit.lon) {
      const emptyUnits = groupMap.get('empty') ?? [] as unknown as typeof units;
      emptyUnits.push(unit);

      groupMap.set('empty', emptyUnits);
      continue;
    }

    const stateUnits = groupMap.get(unit.state) ?? [] as unknown as typeof units;
    stateUnits.push(unit);

    groupMap.set(unit.state, stateUnits);
  }

  return groupMap;
}

export function parseMapInfo(unit: HealthUnitsInfo[number] | ThermalComfortUnitsInfo[number]): MapUnitInfo {
  return {
    id: String(unit.id),
    latitude: unit.lat ?? 0,
    longitude: unit.lon ?? 0,
    name: unit.name,
    city: unit.city,
    state: unit.state,
    color: parseUnitColor(unit),
  };
}

export function parseMapValues(map: UnitMap): Map<string, MapUnitInfo[]> {
  const values: [string, MapUnitInfo[]][] = Array.from(map.entries()).map(([key, value]) => [
    key,
    value.map(parseMapInfo),
  ]);

  return new Map(values);
}

function parseUnitColor(unit: HealthUnitsInfo[number] | ThermalComfortUnitsInfo[number]): string {
  if ('health' in unit) {
    return parseHealthColor(unit.health);
  }

  return parseThermalComfortColor(unit.thermalComfort);
}
